import React, { useState, useCallback, useEffect } from 'react';
import { DragDropContext } from '@hello-pangea/dnd';
import { AlertTriangle } from 'lucide-react';
import { useNavigate } from "react-router-dom";
import { useTasks } from '../context/TaskContext';
import Header from '../components/Header';
import TaskColumn from '../components/TaskColumn';
import TaskModal from '../components/TaskModal';
import { formatDate } from '../components/TaskCard';

const COLS = ['To Do', 'In Progress', 'Completed'];
const PRIORITIES = ['All', 'High', 'Medium', 'Low'];
const CATEGORIES = ['All', 'Work', 'Study', 'Personal', 'Other'];

const StatCard = ({ label, value, sub, accent }) => (
  <div className="bg-white dark:bg-[#1e2235] border border-gray-200 dark:border-gray-700/60 rounded-xl p-4">
    <p className="text-xs text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-1">{label}</p>
    <p className={`text-2xl font-bold ${accent}`}>{value}</p>
    {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
  </div>
);


const exportCSV = () => {
  const tasks = JSON.parse(localStorage.getItem("tasks")) || [];

  const csv = [
    ["Title", "Priority", "Deadline"],
    ...tasks.map((t) => [t.title, t.priority, t.deadline]),
  ]
    .map((row) => row.join(","))
    .join("\n");

  const blob = new Blob([csv], { type: "text/csv" });

  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = "tasks.csv";
  link.click();
};


const Dashboard = () => {
  const navigate = useNavigate();

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("currentUser"));

    if (!user) {
      navigate("/");
    }
  }, []);
  const { tasks, moveTask, reorderTasks, getDeadlineStatus, alertTasks, settings } = useTasks();
  const [search, setSearch] = useState('');
  const [filterPriority, setFilterPriority] = useState('All');
  const [filterCategory, setFilterCategory] = useState('All');
  const [modal, setModal] = useState(null);

  const total = tasks.length;
  const completed = tasks.filter(t => t.status === 'Completed').length;
  const inProgress = tasks.filter(t => t.status === 'In Progress').length;
  const overdue = tasks.filter(t => getDeadlineStatus(t.deadline, t.status) === 'overdue').length;
  const progress = total ? Math.round((completed / total) * 100) : 0;

  const filtered = tasks.filter(t => {
    if (search && !t.title.toLowerCase().includes(search.toLowerCase()) && !t.category.toLowerCase().includes(search.toLowerCase())) return false;
    if (filterPriority !== 'All' && t.priority !== filterPriority) return false;
    if (filterCategory !== 'All' && t.category !== filterCategory) return false;
    return true;
  });

  const onDragEnd = useCallback((result) => {
    const { source, destination, draggableId } = result;
    if (!destination) return;
    if (source.droppableId === destination.droppableId && source.index === destination.index) return;
    if (source.droppableId !== destination.droppableId) {
      moveTask(draggableId, destination.droppableId);
    } else {
      const colTasks = tasks.filter(t => t.status === source.droppableId);
      const otherTasks = tasks.filter(t => t.status !== source.droppableId);
      const [moved] = colTasks.splice(source.index, 1);
      colTasks.splice(destination.index, 0, moved);
      reorderTasks([...otherTasks, ...colTasks]);
    }
  }, [tasks, moveTask, reorderTasks]);

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <Header search={search} setSearch={setSearch} onAddTask={() => setModal({ type: 'add' })} pageTitle="Dashboard" />

      <div className="flex-1 overflow-y-auto scrollbar-thin p-5 space-y-4">
        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <StatCard label="Total Tasks" value={total} sub="All time" accent="text-indigo-400" />
          <StatCard label="Completed" value={completed} sub={`${progress}% done`} accent="text-emerald-400" />
          <StatCard label="In Progress" value={inProgress} sub="Active now" accent="text-amber-400" />
          <StatCard label="Overdue" value={overdue} sub="Need attention" accent="text-red-400" />
        </div>

        {/* Progress bar */}
        <div>
          <div className="flex justify-between text-xs text-gray-400 mb-1.5">
            <span>Overall Progress</span>
            <span className="font-semibold text-indigo-400">{progress}% Complete</span>
          </div>
          <div className="w-full h-2 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-indigo-500 to-emerald-400 rounded-full transition-all duration-700" style={{ width: `${progress}%` }} />
          </div>
        </div>

        {/* Alerts */}
        {settings.notifications && alertTasks.length > 0 && (
          <div className="bg-red-50 dark:bg-red-500/5 border border-red-200 dark:border-red-500/20 rounded-xl p-3.5">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle size={14} className="text-red-400" />
              <span className="text-xs font-semibold text-red-400">{alertTasks.length} Alert{alertTasks.length > 1 ? 's' : ''}</span>
            </div>
            <div className="space-y-1">
              {alertTasks.map(t => {
                const ds = getDeadlineStatus(t.deadline, t.status);
                return (
                  <p key={t.id} className="text-xs text-gray-500 dark:text-gray-400">
                    <span className="mr-1">{ds === 'overdue' ? '🔴' : '🟡'}</span>
                    <span className="font-medium text-gray-700 dark:text-gray-200">{t.title}</span>
                    {' — '}{ds === 'overdue' ? 'Overdue since ' : 'Due '}{formatDate(t.deadline)}
                  </p>
                );
              })}
            </div>
          </div>
        )}

        {/* Filters */}
        <button
          onClick={exportCSV}
          className="ml-2 px-3 py-1 bg-indigo-500 text-white rounded text-xs"
        >
          Export Tasks
        </button>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-gray-400 font-medium">Filter:</span>
          <select
            value={filterPriority}
            onChange={e => setFilterPriority(e.target.value)}
            className="text-xs px-2.5 py-1.5 bg-gray-100 dark:bg-[#1e2235] border border-gray-200 dark:border-gray-700 rounded-lg text-gray-600 dark:text-gray-300 outline-none cursor-pointer focus:border-indigo-500 transition-colors"
          >
            {PRIORITIES.map(p => <option key={p} value={p}>{p === 'All' ? 'All Priorities' : p}</option>)}
          </select>
          <select
            value={filterCategory}
            onChange={e => setFilterCategory(e.target.value)}
            className="text-xs px-2.5 py-1.5 bg-gray-100 dark:bg-[#1e2235] border border-gray-200 dark:border-gray-700 rounded-lg text-gray-600 dark:text-gray-300 outline-none cursor-pointer focus:border-indigo-500 transition-colors"
          >
            {CATEGORIES.map(c => <option key={c} value={c}>{c === 'All' ? 'All Categories' : c}</option>)}
          </select>
          {(filterPriority !== 'All' || filterCategory !== 'All' || search) && (
            <button
              onClick={() => { setFilterPriority('All'); setFilterCategory('All'); setSearch(''); }}
              className="text-xs px-2.5 py-1.5 text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10 rounded-lg transition-colors"
            >
              Clear filters
            </button>
          )}
          <span className="ml-auto text-xs text-gray-400">{filtered.length} task{filtered.length !== 1 ? 's' : ''}</span>
        </div>

        {/* Kanban board */}
        <DragDropContext onDragEnd={onDragEnd}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {COLS.map(col => (
              <TaskColumn
                key={col}
                col={col}
                tasks={filtered.filter(t => t.status === col)}
                onAddTask={(status) => setModal({ type: 'add', status })}
                onEditTask={(task) => setModal({ type: 'edit', task })}
              />
            ))}
          </div>
        </DragDropContext>
      </div>

      {modal && <TaskModal modal={modal} onClose={() => setModal(null)} />}
    </div>
  );
};

export default Dashboard;
